import clsx from "clsx"
import Hls from "hls.js"
import Plyr from "plyr"
import "plyr/dist/plyr.css"
import { forwardRef, useEffect, useRef } from "react"

import waitForBytescaleAuthSession from "@utils/waitForBytescaleAuthSession"

import { MS_PER_SECOND } from "@constants/time"

import styles from "./VideoPlayer.module.scss"
import { VideoPlayerProps } from "./VideoPlayer.types"

const AUTO_QUALITY = 0
const DEFAULT_QUALITY = 720
const HLS_MAX_BUFFER_LENGTH_SECONDS = 30
const HLS_MAX_MAX_BUFFER_LENGTH_SECONDS = 60
const HLS_MANIFEST_TIMEOUT_MS = 5 * MS_PER_SECOND
const MAX_HLS_RETRIES = 3
const PLAYBACK_SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2]

function VideoPlayer(props: VideoPlayerProps, ref: React.Ref<HTMLDivElement>) {
    const { src, streamingSrc, filename, className } = props

    const videoRef = useRef<HTMLVideoElement>(null)
    const playerRef = useRef<Plyr | null>(null)
    const hlsRef = useRef<Hls | null>(null)

    useEffect(() => {
        const video = videoRef.current
        if (!video) {
            return
        }

        const plyrOptions = {
            controls: ["play-large", "play", "progress", "current-time", "mute", "volume", "settings", "fullscreen"],
            settings: ["quality", "speed"],
            speed: { selected: 1, options: PLAYBACK_SPEED_OPTIONS },
        }

        let hlsTimeout: ReturnType<typeof setTimeout> | null = null
        let isUnmounted = false

        if (streamingSrc && Hls.isSupported()) {
            const hls = new Hls({
                maxBufferLength: HLS_MAX_BUFFER_LENGTH_SECONDS,
                maxMaxBufferLength: HLS_MAX_MAX_BUFFER_LENGTH_SECONDS,
            })

            hlsRef.current = hls

            let hlsInitialized = false
            let plyrInitialized = false
            let hlsRetryCount = 0

            const fallbackToDirectPlayback = () => {
                if (hlsRef.current) {
                    hlsRef.current.destroy()
                    hlsRef.current = null
                }
                video.src = src
                if (!plyrInitialized) {
                    plyrInitialized = true
                    playerRef.current = new Plyr(video, plyrOptions)
                }
            }

            const startStreaming = async () => {
                // On fresh page loads the auth session may still be establishing; starting early
                // would burn the HLS retries on 401s and drop into direct playback
                await waitForBytescaleAuthSession()
                if (isUnmounted) {
                    return
                }

                hls.loadSource(streamingSrc)
                hls.attachMedia(video)

                hlsTimeout = setTimeout(() => {
                    if (!hlsInitialized) {
                        console.warn("HLS manifest load timeout, falling back to direct playback")
                        fallbackToDirectPlayback()
                    }
                }, HLS_MANIFEST_TIMEOUT_MS)
            }

            void startStreaming()

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (hlsTimeout) {
                    clearTimeout(hlsTimeout)
                }
                if (hlsInitialized) {
                    return
                }
                hlsInitialized = true

                // Extract available quality levels from the HLS manifest
                const hlsLevels = hls.levels
                const qualityHeights = hlsLevels.map((level) => level.height).filter(Boolean)

                // Build quality options: Auto first, then resolutions from highest to lowest
                const sortedQualities = [AUTO_QUALITY, ...qualityHeights.sort((a, b) => b - a)]
                const uniqueQualities = [...new Set(sortedQualities)]

                // Default to Auto if DEFAULT_QUALITY is not available in the manifest
                const defaultQuality = qualityHeights.includes(DEFAULT_QUALITY) ? DEFAULT_QUALITY : AUTO_QUALITY

                // Start in ABR mode
                hls.currentLevel = -1

                const player = new Plyr(video, {
                    ...plyrOptions,
                    i18n: {
                        qualityLabel: {
                            [AUTO_QUALITY]: "Auto",
                        },
                    },
                    quality: {
                        default: defaultQuality,
                        options: uniqueQualities,
                        forced: true,
                        onChange: (quality: number) => {
                            if (!hlsRef.current) {
                                return
                            }

                            if (quality === AUTO_QUALITY) {
                                // Enable hls.js adaptive bitrate (ABR) mode
                                hlsRef.current.nextLevel = -1
                            } else {
                                // Switch quality at next segment boundary (no buffer flush)
                                const levelIndex = hlsRef.current.levels.findIndex((level) => level.height === quality)
                                if (levelIndex !== -1) {
                                    hlsRef.current.nextLevel = levelIndex
                                }
                            }
                        },
                    },
                })
                playerRef.current = player
                plyrInitialized = true
            })

            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data.fatal) {
                    if (hlsTimeout) {
                        clearTimeout(hlsTimeout)
                    }
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            hlsRetryCount++
                            if (hlsRetryCount <= MAX_HLS_RETRIES) {
                                hls.startLoad()
                            } else {
                                console.warn("HLS fatal network error, falling back to direct playback:", data.details)
                                fallbackToDirectPlayback()
                            }
                            break
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError()
                            break
                        default:
                            console.warn("HLS fatal error, falling back to direct playback:", data.details)
                            fallbackToDirectPlayback()
                            break
                    }
                }
            })
        } else if (streamingSrc) {
            // Native HLS (Safari) — browser handles HLS natively; unlike hls.js it won't retry a
            // failed stream, so fall back to the direct file on the first media error
            video.addEventListener(
                "error",
                () => {
                    video.src = src
                    video.load()
                },
                { once: true },
            )
            video.src = streamingSrc
            playerRef.current = new Plyr(video, plyrOptions)
        } else {
            // Direct file playback (no streaming URL available)
            video.src = src
            playerRef.current = new Plyr(video, plyrOptions)
        }

        return () => {
            isUnmounted = true
            if (hlsTimeout) {
                clearTimeout(hlsTimeout)
            }
            if (hlsRef.current) {
                hlsRef.current.destroy()
                hlsRef.current = null
            }
            if (playerRef.current) {
                playerRef.current.destroy()
                playerRef.current = null
            }
        }
        // Initialize once per mount: Plyr's destroy() replaces the video element with a detached
        // clone, so a re-run would rebuild the player off-document. A new attachment mounts a
        // fresh player (keyed by attachment id); a re-signed URL for the same file needs no reload.
    }, [])

    return (
        <div ref={ref} className={clsx(styles.container, className)}>
            <video ref={videoRef} className={styles.video} playsInline={true} title={filename}>
                Your browser does not support the video tag.
            </video>
        </div>
    )
}

export default forwardRef(VideoPlayer)
